import React, { useEffect, useState } from 'react';
import { BarChart2, Clock, Heart, Lock, MessageCircle, MoreHorizontal, Share2, Trophy, Users } from 'lucide-react';
type PollOption = {
  id: string;
  text: string;
  image?: string;
};
interface PollCardProps {
  id: string;
  question: string;
  options: PollOption[];
  createdAt: string;
  endsAt: string;
  totalVotes: number;
  userVote?: string;
  createdBy: {
    name: string;
    avatar: string;
    verified?: boolean;
  };
  isRitual?: boolean;
  ritualId?: string;
  onVote: (pollId: string, optionId: string) => void;
}
export const PollCard: React.FC<PollCardProps> = ({
  id,
  question,
  options,
  createdAt,
  endsAt,
  totalVotes,
  userVote,
  createdBy,
  isRitual = false,
  ritualId,
  onVote
}) => {
  const [selectedOption, setSelectedOption] = useState<string | undefined>(userVote);
  const [showResults, setShowResults] = useState(!!userVote);
  const [timeLeft, setTimeLeft] = useState('');
  const [optionPercentages, setOptionPercentages] = useState<Record<string, number>>({});
  // Calculate time left
  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      const end = new Date(endsAt);
      const diff = end.getTime() - now.getTime();
      if (diff <= 0) {
        setTimeLeft('Ended');
        setShowResults(true);
        return;
      }
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor(diff % (1000 * 60 * 60 * 24) / (1000 * 60 * 60));
      const minutes = Math.floor(diff % (1000 * 60 * 60) / (1000 * 60));
      if (days > 0) {
        setTimeLeft(`${days}d ${hours}h left`);
      } else if (hours > 0) {
        setTimeLeft(`${hours}h ${minutes}m left`);
      } else {
        setTimeLeft(`${minutes}m left`);
      }
    };
    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 60000);
    return () => clearInterval(timer);
  }, [endsAt]);
  // Calculate percentages
  useEffect(() => {
    if (totalVotes === 0) {
      const equalPercentage = 100 / options.length;
      const percentages: Record<string, number> = {};
      options.forEach(option => {
        percentages[option.id] = equalPercentage;
      });
      setOptionPercentages(percentages);
    } else {
      // In a real app, you would get the actual vote counts from the backend
      // This is just a simulation
      const percentages: Record<string, number> = {};
      options.forEach((option, index) => {
        // Simulate percentages based on option index
        const basePercentage = 100 / options.length;
        const variance = Math.sin(index) * 15; // Add some variance
        percentages[option.id] = Math.max(5, Math.min(70, basePercentage + variance));
      });
      // If user has voted, make sure their option has a decent percentage
      if (selectedOption) {
        percentages[selectedOption] = Math.max(percentages[selectedOption], 25);
      }
      // Normalize percentages to sum to 100
      const total = Object.values(percentages).reduce((a, b) => a + b, 0);
      Object.keys(percentages).forEach(key => {
        percentages[key] = Math.round(percentages[key] / total * 100);
      });
      setOptionPercentages(percentages);
    }
  }, [options, totalVotes, selectedOption]);
  const handleVote = (optionId: string) => {
    if (selectedOption || timeLeft === 'Ended') return;
    setSelectedOption(optionId);
    setShowResults(true);
    onVote(id, optionId);
    // If this is a ritual poll, trigger ritual progress update
    if (isRitual && ritualId) {
      const event = new CustomEvent('ritualPollVoted', {
        detail: {
          ritualId,
          pollId: id,
          optionId
        }
      });
      document.dispatchEvent(event);
    }
  };
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    if (minutes < 60) {
      return `${minutes}m ago`;
    } else if (hours < 24) {
      return `${hours}h ago`;
    } else {
      return `${days}d ago`;
    }
  };
  return <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200" data-id="element-15480">
      <div className="p-4 border-b border-gray-200 dark:border-gray-800" data-id="element-15481">
        <div className="flex items-center justify-between" data-id="element-15482">
          <div className="flex items-center gap-3" data-id="element-15483">
            <div className="w-10 h-10 rounded-full overflow-hidden" data-id="element-15484">
              <img src={createdBy.avatar} alt={createdBy.name} className="w-full h-full object-cover" data-id="element-15485" />
            </div>
            <div data-id="element-15486">
              <div className="flex items-center gap-1" data-id="element-15487">
                <span className="font-medium text-gray-900 dark:text-white" data-id="element-15488">
                  {createdBy.name}
                </span>
                {createdBy.verified && <span className="text-blue-500" data-id="element-15489">✓</span>}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1" data-id="element-15490">
                <span data-id="element-15491">{formatDate(createdAt)}</span>
                {isRitual && <>
                    <span data-id="element-15492">•</span>
                    <div className="flex items-center gap-1 text-pink-500" data-id="element-15493">
                      <Trophy size={12} data-id="element-15494" />
                      <span data-id="element-15495">Ritual Poll</span>
                    </div>
                  </>}
              </div>
            </div>
          </div>
          <button className="p-2 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800" data-id="element-15496">
            <MoreHorizontal size={18} data-id="element-15497" />
          </button>
        </div>
      </div>
      <div className="p-4" data-id="element-15498">
        <p className="text-gray-900 dark:text-white mb-4" data-id="element-15499">{question}</p>
        <div className="space-y-2 mb-4" data-id="element-15500">
          {options.map(option => <button key={option.id} onClick={() => handleVote(option.id)} disabled={!!selectedOption || timeLeft === 'Ended'} className={`relative w-full p-3 rounded-lg border text-left transition-all ${selectedOption === option.id ? 'border-pink-500 dark:border-pink-400 bg-pink-50 dark:bg-pink-900/20' : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600'} ${selectedOption && selectedOption !== option.id ? 'opacity-70' : ''}`} data-id="element-15501">
              <div className="flex justify-between items-center" data-id="element-15502">
                <span className={`${selectedOption === option.id ? 'text-gray-900 dark:text-white font-medium' : 'text-gray-700 dark:text-gray-300'}`} data-id="element-15503">
                  {option.text}
                </span>
                {showResults && <span className="text-xs font-medium bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-2 py-1 rounded-full" data-id="element-15504">
                    {optionPercentages[option.id] || 0}%
                  </span>}
              </div>
              {showResults && <div className="absolute bottom-0 left-0 h-full overflow-hidden rounded-lg" data-id="element-15505">
                  <div className={`h-full ${selectedOption === option.id ? 'bg-pink-500/20 dark:bg-pink-500/30' : 'bg-gray-200/50 dark:bg-gray-700/50'}`} style={{
              width: `${optionPercentages[option.id] || 0}%`
            }} data-id="element-15506"></div>
                </div>}
            </button>)}
        </div>
        <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400" data-id="element-15507">
          <div className="flex items-center gap-4" data-id="element-15508">
            <div className="flex items-center gap-1" data-id="element-15509">
              <BarChart2 size={16} data-id="element-15510" />
              <span data-id="element-15511">{totalVotes} votes</span>
            </div>
            <div className="flex items-center gap-1" data-id="element-15512">
              <MessageCircle size={16} data-id="element-15513" />
              <span data-id="element-15514">12 comments</span>
            </div>
          </div>
          <div className="flex items-center gap-1" data-id="element-15515">
            <Clock size={16} data-id="element-15516" />
            <span data-id="element-15517">{timeLeft}</span>
          </div>
        </div>
        {isRitual && !selectedOption && timeLeft !== 'Ended' && <div className="mt-4 bg-pink-50 dark:bg-pink-900/20 rounded-lg p-3 border border-pink-100 dark:border-pink-800/30" data-id="element-15518">
            <div className="flex items-center gap-2" data-id="element-15519">
              <Trophy size={16} className="text-pink-500" data-id="element-15520" />
              <span className="text-sm text-gray-700 dark:text-gray-300" data-id="element-15521">
                Vote to help complete a ritual challenge
              </span>
              <div className="ml-auto flex items-center gap-1 text-pink-500" data-id="element-15522">
                <Heart size={14} fill="currentColor" data-id="element-15523" />
                <span className="text-xs font-medium" data-id="element-15524">+5</span>
              </div>
            </div>
          </div>}
        {selectedOption && isRitual && <div className="mt-4 bg-green-50 dark:bg-green-900/20 rounded-lg p-3 border border-green-100 dark:border-green-800/30" data-id="element-15525">
            <div className="flex items-center gap-2" data-id="element-15526">
              <Trophy size={16} className="text-green-500" data-id="element-15527" />
              <span className="text-sm text-gray-700 dark:text-gray-300" data-id="element-15528">
                Thanks for voting! You've earned 5 tokens.
              </span>
              <div className="ml-auto flex items-center gap-1 text-green-500" data-id="element-15529">
                <Heart size={14} fill="currentColor" data-id="element-15530" />
                <span className="text-xs font-medium" data-id="element-15531">+5</span>
              </div>
            </div>
          </div>}
      </div>
      <div className="p-3 border-t border-gray-200 dark:border-gray-800 flex items-center justify-between" data-id="element-15532">
        <div className="flex items-center gap-3" data-id="element-15533">
          <button className="flex items-center gap-1 text-gray-500 dark:text-gray-400 hover:text-pink-500 dark:hover:text-pink-400" data-id="element-15534">
            <Heart size={18} data-id="element-15535" />
            <span className="text-xs" data-id="element-15536">Like</span>
          </button>
          <button className="flex items-center gap-1 text-gray-500 dark:text-gray-400 hover:text-blue-500 dark:hover:text-blue-400" data-id="element-15537">
            <MessageCircle size={18} data-id="element-15538" />
            <span className="text-xs" data-id="element-15539">Comment</span>
          </button>
        </div>
        <button className="flex items-center gap-1 text-gray-500 dark:text-gray-400 hover:text-green-500 dark:hover:text-green-400" data-id="element-15540">
          <Share2 size={18} data-id="element-15541" />
          <span className="text-xs" data-id="element-15542">Share</span>
        </button>
      </div>
    </div>;
};